import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';
import { StatoProfilo, RegimeFiscale, type FiscalDocument, type FiscalDocumentType, type FiscalDocuments } from '@klaro7/shared-types';

import { DateOfBirthInput } from '@/components/DateOfBirthInput';
import { extractErrorMessage } from '@/lib/api';
import { ContractsSection } from '@/features/contracts/ContractsSection';
import {
  useFiscalProfile,
  useUpdateFiscalProfile,
  useUploadFiscalDocument,
  useDeleteFiscalDocument,
  fetchFiscalDocumentBlob,
} from './api';

// ── Formatting helpers ────────────────────────────────────────────────────────

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function isoToInputDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0] ?? '';
}

// ── Regime options ────────────────────────────────────────────────────────────

const REGIME_OPTIONS: { value: RegimeFiscale; label: string }[] = [
  { value: RegimeFiscale.OCCASIONALE, label: 'Prestazione occasionale' },
  { value: RegimeFiscale.FORFETTARIO, label: 'P.IVA forfettario' },
  { value: RegimeFiscale.ORDINARIO, label: 'P.IVA ordinario' },
  { value: RegimeFiscale.CON_CASSA, label: 'P.IVA con cassa professionale' },
  { value: RegimeFiscale.ESTERO, label: 'P.IVA estera (fuori Italia)' },
];

// ── YTD Progress bar ──────────────────────────────────────────────────────────

const YTD_LIMIT = 5000;

// Match the email alert thresholds in apps/api/src/modules/fiscal/threshold-mailer.ts —
// keep these in sync if the backend values change.
const ALERT_THRESHOLDS: { value: number; label: string }[] = [
  { value: 3000, label: '€3.000' },
  { value: 4000, label: '€4.000' },
  { value: 4500, label: '€4.500' },
];

function YtdProgressBar({ lordoYtd }: { lordoYtd: number }) {
  const pct = Math.min(100, Math.round((lordoYtd / YTD_LIMIT) * 100));
  const isNearLimit = pct >= 80;
  const barColor = isNearLimit ? 'bg-brand-orange' : 'bg-brand-teal';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-700">Lordo anno corrente</span>
        <span className="font-semibold text-neutral-900">
          {formatEur(lordoYtd)}{' '}
          <span className="font-normal text-neutral-500">/ {formatEur(YTD_LIMIT)}</span>
        </span>
      </div>
      <div className="relative pb-5">
        <div className="relative h-3 w-full rounded-full bg-neutral-200">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
          {ALERT_THRESHOLDS.map((t) => {
            const reached = lordoYtd >= t.value;
            const tickPct = (t.value / YTD_LIMIT) * 100;
            const tooltip = reached
              ? `Soglia ${t.label} superata — email di avviso già inviata`
              : `Email di avviso quando il lordo raggiunge ${t.label}`;
            return (
              <span
                key={t.value}
                className="group absolute top-0 flex h-full -translate-x-1/2 cursor-help items-center justify-center"
                style={{ left: `${tickPct}%` }}
              >
                {/* invisible hover target so the 1px tick is easy to point at */}
                <span className="absolute inset-y-0 -inset-x-1.5" aria-hidden="true" />
                <span
                  role="img"
                  aria-label={tooltip}
                  title={tooltip}
                  className={`block h-full w-px ${reached ? 'bg-white/70' : 'bg-neutral-400'}`}
                />
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  {tooltip}
                </span>
              </span>
            );
          })}
        </div>
        {ALERT_THRESHOLDS.map((t) => {
          const reached = lordoYtd >= t.value;
          const tickPct = (t.value / YTD_LIMIT) * 100;
          const tooltip = reached
            ? `Soglia ${t.label} superata — email di avviso già inviata`
            : `Email di avviso quando il lordo raggiunge ${t.label}`;
          return (
            <span
              key={t.value}
              className="group absolute top-full mt-0.5 -translate-x-1/2 cursor-help text-[10px] font-medium text-neutral-500"
              style={{ left: `${tickPct}%` }}
              title={tooltip}
            >
              {t.label}
              <span
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
              >
                {tooltip}
              </span>
            </span>
          );
        })}
      </div>
      <p className="text-xs text-neutral-500">
        {pct}% del limite provvisorio di {formatEur(YTD_LIMIT)} raggiunto. Email di avviso a €3.000, €4.000 e €4.500.
      </p>
    </div>
  );
}

// ── Identity documents ──────────────────────────────────────────────────────────

const ACCEPTED_DOC_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_DOC_MB = 5;

function DocumentSlot({
  docType,
  label,
  doc,
}: {
  docType: FiscalDocumentType;
  label: string;
  doc: FiscalDocument | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadFiscalDocument();
  const remove = useDeleteFiscalDocument();
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);

  const busy = upload.isPending || remove.isPending;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_DOC_MB * 1024 * 1024) {
      setError(`Il file supera ${MAX_DOC_MB} MB.`);
      return;
    }
    try {
      await upload.mutateAsync({ docType, file });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleView = async () => {
    setError(null);
    setViewing(true);
    try {
      const blob = await fetchFiscalDocumentBlob(docType);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke a bit later so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Impossibile aprire il documento.');
    } finally {
      setViewing(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      await remove.mutateAsync(docType);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-800">{label}</p>
          {doc ? (
            <p className="mt-0.5 truncate text-xs text-neutral-500" title={doc.originalName}>
              {doc.originalName}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-neutral-400">Nessun file caricato</p>
          )}
        </div>
        {doc ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Caricato
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_DOC_TYPES}
          onChange={(e) => void handleFile(e)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-brand-teal/10 px-2.5 py-1 text-xs font-medium text-brand-teal hover:bg-brand-teal/20 disabled:opacity-60 transition-colors"
        >
          {upload.isPending ? <Spinner size="sm" /> : null}
          {doc ? 'Sostituisci' : 'Carica'}
        </button>
        {doc && (
          <>
            <button
              type="button"
              onClick={() => void handleView()}
              disabled={viewing}
              className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-60 transition-colors"
            >
              {viewing ? <Spinner size="sm" /> : null}
              Visualizza
            </button>
            <button
              type="button"
              onClick={() => void handleRemove()}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors"
            >
              {remove.isPending ? <Spinner size="sm" /> : null}
              Rimuovi
            </button>
          </>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function DocumentsCard({ documenti, showVisura }: { documenti: FiscalDocuments; showVisura: boolean }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="mb-1 text-base font-semibold text-neutral-900">Documenti</h2>
      <p className="mb-5 text-sm text-neutral-500">
        Carica carta d'identità e codice fiscale (fronte e retro){showVisura ? ' e la visura camerale della P.IVA' : ''}.
        Formati: JPG, PNG, WebP o PDF, max {MAX_DOC_MB} MB.
        I documenti sono riservati e visibili solo a te e all'amministrazione.
      </p>

      <div className="space-y-5">
        <div>
          <p className="mb-2 text-sm font-medium text-neutral-700">Carta d'identità</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DocumentSlot docType="ci_fronte" label="Fronte" doc={documenti.ci_fronte} />
            <DocumentSlot docType="ci_retro" label="Retro" doc={documenti.ci_retro} />
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-neutral-700">Tessera codice fiscale</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DocumentSlot docType="cf_fronte" label="Fronte" doc={documenti.cf_fronte} />
            <DocumentSlot docType="cf_retro" label="Retro" doc={documenti.cf_retro} />
          </div>
        </div>
        {showVisura && (
          <div>
            <p className="mb-2 text-sm font-medium text-neutral-700">Visura camerale P.IVA</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <DocumentSlot docType="visura" label="Visura" doc={documenti.visura} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function FiscalProfilePage() {
  const { data: profile, isLoading: profileLoading } = useFiscalProfile();
  const updateProfile = useUpdateFiscalProfile();

  const [form, setForm] = useState({
    codiceFiscale: '',
    dataNascita: '',
    comuneNascita: '',
    indirizzoResidenza: '',
    regimeFiscale: RegimeFiscale.OCCASIONALE,
    partitaIVA: '',
    paeseEstero: '',
    cassaNome: '',
    cassaPercentuale: '',
    ragioneSociale: '',
    sedeFiscale: '',
    numeroREA: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);

  // Populate form when profile loads
  useEffect(() => {
    if (!profile) return;
    setForm({
      codiceFiscale: profile.codiceFiscale ?? '',
      dataNascita: isoToInputDate(profile.dataNascita),
      comuneNascita: profile.comuneNascita ?? '',
      indirizzoResidenza: profile.indirizzoResidenza ?? '',
      regimeFiscale: profile.regimeFiscale ?? RegimeFiscale.OCCASIONALE,
      partitaIVA: profile.partitaIVA ?? '',
      paeseEstero: profile.paeseEstero ?? '',
      cassaNome: profile.cassaProfessionale?.nome ?? '',
      cassaPercentuale: profile.cassaProfessionale?.percentuale != null
        ? String(profile.cassaProfessionale.percentuale)
        : '',
      ragioneSociale: profile.ragioneSociale ?? '',
      sedeFiscale: profile.sedeFiscale ?? '',
      numeroREA: profile.numeroREA ?? '',
    });
  }, [profile]);

  const isOccasionale = form.regimeFiscale === RegimeFiscale.OCCASIONALE;
  const isEstero = form.regimeFiscale === RegimeFiscale.ESTERO;
  const needsPartitaIVA = !isOccasionale;
  const needsCassa = form.regimeFiscale === RegimeFiscale.CON_CASSA;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);
    try {
      const payload = {
        codiceFiscale: form.codiceFiscale,
        dataNascita: form.dataNascita,
        comuneNascita: form.comuneNascita,
        indirizzoResidenza: form.indirizzoResidenza,
        regimeFiscale: form.regimeFiscale,
        ...(needsPartitaIVA
          ? {
              partitaIVA: form.partitaIVA,
              ragioneSociale: form.ragioneSociale || null,
              sedeFiscale: form.sedeFiscale || null,
              numeroREA: form.numeroREA || null,
            }
          : {}),
        ...(isEstero ? { paeseEstero: form.paeseEstero } : {}),
        ...(needsCassa
          ? {
              cassaProfessionale: {
                nome: form.cassaNome,
                percentuale: parseFloat(form.cassaPercentuale) || 0,
              },
            }
          : { cassaProfessionale: null }),
      };
      await updateProfile.mutateAsync(payload);
      setFormSuccess(true);
    } catch (err) {
      setFormError(extractErrorMessage(err));
    }
  };

  const isSospeso = profile?.statoProfilo === StatoProfilo.SOSPESO;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Profilo fiscale</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Gestisci i tuoi dati fiscali, i documenti d'identità e monitora il limite provvisorio.
        </p>
      </div>

      {/* Suspension banner — only for occasionale */}
      {isSospeso && isOccasionale && (
        <div className="flex items-start gap-3 rounded-xl bg-brand-orange px-5 py-4 text-white shadow">
          <svg className="mt-0.5 h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div>
            <p className="font-semibold">Profilo sospeso</p>
            <p className="mt-0.5 text-sm text-white/90">
              Hai raggiunto il limite di € 5.000 della modalità provvisoria. Apri una partita IVA e
              selezionala qui sotto nel regime fiscale: la sospensione viene rimossa al salvataggio.
            </p>
          </div>
        </div>
      )}

      {/* YTD progress — only for occasionale */}
      {isOccasionale && (
        profileLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : profile ? (
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h2 className="mb-4 text-base font-semibold text-neutral-900">Andamento anno corrente</h2>
            <YtdProgressBar lordoYtd={profile.lordoYtd} />
          </div>
        ) : null
      )}

      {/* Fiscal data form */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <h2 className="mb-1 text-base font-semibold text-neutral-900">Dati anagrafici e fiscali</h2>
        <p className="mb-5 text-sm text-neutral-500">
          Necessari per emettere le ricevute. Il codice fiscale è obbligatorio per ricevere i pagamenti.
        </p>

        {!profile?.isComplete && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Completa il tuo profilo fiscale per sbloccare i pagamenti e poter ricevere chiamate. Finché manca, non puoi
            essere mostrato online ai clienti.
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {/* Regime dropdown */}
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="regimeFiscale">
              Modalità fiscale
            </label>
            <select
              id="regimeFiscale"
              value={form.regimeFiscale}
              onChange={(e) =>
                setForm((f) => ({ ...f, regimeFiscale: e.target.value as RegimeFiscale }))
              }
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              {REGIME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Anagrafica fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="codiceFiscale">
                {isEstero ? 'Identificativo fiscale estero' : 'Codice fiscale'}
              </label>
              <input
                id="codiceFiscale"
                type="text"
                maxLength={isEstero ? 32 : 16}
                value={form.codiceFiscale}
                onChange={(e) => setForm((f) => ({ ...f, codiceFiscale: e.target.value.toUpperCase() }))}
                placeholder={isEstero ? 'es. X1234567B (NIE/NIF, Steuer-ID…)' : 'RSSMRA80A01H501U'}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                required
              />
            </div>

            <div>
              <DateOfBirthInput
                id="dataNascita"
                label="Data di nascita"
                value={form.dataNascita}
                onChange={(iso) => setForm((f) => ({ ...f, dataNascita: iso }))}
                className="focus:border-brand-teal focus:ring-1 focus:ring-brand-teal"
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="comuneNascita">
              Comune di nascita
            </label>
            <input
              id="comuneNascita"
              type="text"
              value={form.comuneNascita}
              onChange={(e) => setForm((f) => ({ ...f, comuneNascita: e.target.value }))}
              placeholder="Roma"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="indirizzoResidenza">
              Indirizzo di residenza
            </label>
            <input
              id="indirizzoResidenza"
              type="text"
              value={form.indirizzoResidenza}
              onChange={(e) => setForm((f) => ({ ...f, indirizzoResidenza: e.target.value }))}
              placeholder="Via Roma 1, 00100 Roma"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
              required
            />
          </div>

          {/* Paese di residenza fiscale — solo regime estero */}
          {isEstero && (
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="paeseEstero">
                Paese di residenza fiscale
              </label>
              <input
                id="paeseEstero"
                type="text"
                maxLength={100}
                value={form.paeseEstero}
                onChange={(e) => setForm((f) => ({ ...f, paeseEstero: e.target.value }))}
                placeholder="es. Spagna"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                required
              />
            </div>
          )}

          {/* P.IVA fields — forfettario, ordinario, con_cassa, estero */}
          {needsPartitaIVA && (
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="partitaIVA">
                {isEstero ? 'Partita IVA / VAT estera' : 'Partita IVA'}
              </label>
              <input
                id="partitaIVA"
                type="text"
                maxLength={isEstero ? 20 : 11}
                value={form.partitaIVA}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    partitaIVA: isEstero
                      ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                      : e.target.value.replace(/\D/g, ''),
                  }))
                }
                placeholder={isEstero ? 'es. ESB1234567X' : '12345678901'}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                required
              />
              <p className="mt-1 text-xs text-neutral-500">
                {isEstero
                  ? 'Numero VAT del tuo paese, incluso il prefisso nazionale (es. ES, DE, FR).'
                  : '11 cifre, senza prefisso IT.'}
              </p>
            </div>
          )}

          {/* Dati business — solo regimi P.IVA */}
          {needsPartitaIVA && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-4">
              <p className="text-sm font-medium text-neutral-700">Dati business</p>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="ragioneSociale">
                  Ragione sociale / Denominazione
                </label>
                <input
                  id="ragioneSociale"
                  type="text"
                  value={form.ragioneSociale}
                  onChange={(e) => setForm((f) => ({ ...f, ragioneSociale: e.target.value }))}
                  placeholder="es. Mario Rossi / Rossi Consulting SRL"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="sedeFiscale">
                  Sede fiscale
                </label>
                <input
                  id="sedeFiscale"
                  type="text"
                  value={form.sedeFiscale}
                  onChange={(e) => setForm((f) => ({ ...f, sedeFiscale: e.target.value }))}
                  placeholder="Via Roma 1, 00100 Roma (RM)"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="numeroREA">
                  N. Registro Camera di Commercio (REA)
                </label>
                <input
                  id="numeroREA"
                  type="text"
                  value={form.numeroREA}
                  onChange={(e) => setForm((f) => ({ ...f, numeroREA: e.target.value }))}
                  placeholder="es. RM-1234567"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                />
                <p className="mt-1 text-xs text-neutral-500">Sigla provincia + numero REA. Opzionale.</p>
              </div>
            </div>
          )}

          {/* Cassa professionale — only con_cassa */}
          {needsCassa && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-4">
              <p className="text-sm font-medium text-neutral-700">Cassa professionale</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="cassaNome">
                    Nome cassa
                  </label>
                  <input
                    id="cassaNome"
                    type="text"
                    value={form.cassaNome}
                    onChange={(e) => setForm((f) => ({ ...f, cassaNome: e.target.value }))}
                    placeholder="es. Enpam, Inarcassa…"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700" htmlFor="cassaPercentuale">
                    Percentuale rivalsa (%)
                  </label>
                  <input
                    id="cassaPercentuale"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.cassaPercentuale}
                    onChange={(e) => setForm((f) => ({ ...f, cassaPercentuale: e.target.value }))}
                    placeholder="4"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          {formSuccess && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              Profilo fiscale aggiornato con successo.
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updateProfile.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-orange/90 disabled:opacity-60 transition-colors"
            >
              {updateProfile.isPending && <Spinner size="sm" />}
              Salva dati fiscali
            </button>
          </div>
        </form>
      </div>

      {/* Contracts & compliance documents */}
      <ContractsSection regime={form.regimeFiscale} />

      {/* Identity documents */}
      {profile && <DocumentsCard documenti={profile.documenti} showVisura={needsPartitaIVA} />}

      {/* Payout documents now live on the Compensi page — point the consultant there. */}
      <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">Proforma, ricevute e conferme</h2>
          <p className="mt-0.5 text-sm text-neutral-500">
            I documenti dei tuoi compensi, il caricamento della fattura e la conferma si trovano nella pagina Compensi.
          </p>
        </div>
        <Link
          to="/dashboard/compensi"
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white hover:bg-brand-orange/90 transition-colors"
        >
          Vai a Compensi
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
